import { AddTwoToneIcon, Anchor, Button, ListItemLayout, ListLayout, Panel, XIcon } from '@keplerco/core';
import React, { useState } from 'react';
import { StepSkeleton } from '../../../../components/general/stepper/step/step.skeleton';
import { ISkillStepProps } from './skills-step.models';
import { WidgetCard } from '../../../analytics/analytics-filters/widget-card';
import { SkeletonOverlay } from '../../../../components/general/loading-state/skeleton-overlay';
import { EmptyState } from '../../../../components/general/empty-state/empty-state';
import classNames from 'classnames';
import { SkillsStepEmptyStateImage } from './skills-step-empty-state.image';
import { useAppActions } from '../../../../overmind';
import { Skill } from '../../../../models/refactored/skill';
import { SelectionPanel } from '../../../../components/panels/selection/selection.panel';
import { SkillSelectionList } from './skill-selection-list';

export function SkillsStep({ parentIsLoading, skillCategory, onBack, onNext }: ISkillStepProps): JSX.Element {
  const actions = useAppActions();

  const [isLoading] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedSkills, setSelectedSkills] = useState<Skill[]>(skillCategory?.skills ?? []);
  const [isValid, setIsValid] = useState<boolean>();

  function handleSkillRemove(companySkillSlug: string) {
    setSelectedSkills(currentState => currentState.filter(selectedSkill => selectedSkill.slug !== companySkillSlug));
  }

  if (parentIsLoading || isLoading) return <StepSkeleton />;

  return (
    <React.Fragment>
      <div style={{ width: '100%' }}>
        <SkeletonOverlay isLoading={parentIsLoading || isLoading}>
          <WidgetCard
            title="Skills"
            subtitle="Choose the skills that you would like to group together in this category"
            button={
              <Button fullWidthMobile type="button" square theme="dark" onClick={() => setIsOpen(true)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <AddTwoToneIcon size={16} />
                  Add
                </div>
              </Button>
            }
          >
            {!!selectedSkills.length ? (
              <ListLayout>
                {selectedSkills.map(selectedSkill => (
                  <ListItemLayout key={selectedSkill.slug} onClick={() => handleSkillRemove(selectedSkill.slug!)}>
                    <div className="card">
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: 12 }}>
                        <div>
                          <h5>{selectedSkill.name}</h5>
                          <div style={{ color: 'var(--text_1)' }}>{selectedSkill.description}</div>
                        </div>

                        <XIcon />
                      </div>
                    </div>
                  </ListItemLayout>
                ))}
              </ListLayout>
            ) : (
              <EmptyState imageElement={<SkillsStepEmptyStateImage />} />
            )}

            <div className={classNames('formErrorMessage', { invisible: isValid !== false })}>Please select at least one skill</div>
          </WidgetCard>
        </SkeletonOverlay>

        <footer className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--background)', marginTop: 24 }}>
          <Anchor arrow reverse onClick={onBack}>
            Back
          </Anchor>

          <Button
            type="button"
            arrow
            filled
            onClick={() => {
              if (!skillCategory) return;

              if (!selectedSkills.length) {
                setIsValid(false);
                return;
              }

              const tempSelectedSkills = selectedSkills.filter(selectedSkill => !!selectedSkill.slug);

              actions.updateSkillCategory({ skillCategorySlug: skillCategory.slug, name: skillCategory.name, description: skillCategory.description, skillSlugs: tempSelectedSkills.map(selectedSkill => selectedSkill.slug!) });
              // TODO: error handling

              onNext({
                slug: skillCategory.slug,
                name: skillCategory.name,
                description: skillCategory.description,
                isArchived: skillCategory.isArchived,
                skills: tempSelectedSkills.map(selectedSkill => ({ id: selectedSkill.id, slug: selectedSkill.slug, name: selectedSkill.name, description: selectedSkill.description })),
              });
            }}
          >
            Next
          </Button>
        </footer>
      </div>

      <Panel open={isOpen} onClose={() => setIsOpen(false)} size="fullscreen">
        {isOpen && (
          <SelectionPanel<Skill>
            title="Skills"
            subtitle="Select the skills you'd like to include"
            selectedEntities={selectedSkills.map(selectedSkill => ({ slug: selectedSkill.slug!, name: selectedSkill.name, entity: selectedSkill }))}
            entityType="skills"
            onCancel={() => setIsOpen(false)}
            onAdd={selectedEntities => {
              setSelectedSkills(selectedEntities.map(selectedEntity => selectedEntity.entity));
              setIsOpen(false);
            }}
          >
            {({ selectedEntities, setSelectedEntities }) => <SkillSelectionList selectedEntities={selectedEntities} setSelectedEntities={setSelectedEntities} />}
          </SelectionPanel>
        )}
      </Panel>
    </React.Fragment>
  );
}
